// Available system fonts
const SYSTEM_FONTS = [
  { label: 'Arial', value: 'Arial, sans-serif', isCustom: false },
  { label: 'Helvetica', value: 'Helvetica, sans-serif', isCustom: false },
  {
    label: 'Times New Roman',
    value: '"Times New Roman", serif',
    isCustom: false,
  },
  { label: 'Georgia', value: 'Georgia, serif', isCustom: false },
  { label: 'Verdana', value: 'Verdana, sans-serif', isCustom: false },
  {
    label: 'Trebuchet MS',
    value: '"Trebuchet MS", sans-serif',
    isCustom: false,
  },
  { label: 'Courier New', value: '"Courier New", monospace', isCustom: false },
  { label: 'Impact', value: 'Impact, sans-serif', isCustom: false },
  {
    label: 'Comic Sans MS',
    value: '"Comic Sans MS", cursive',
    isCustom: false,
  },
];

// List of web-safe fonts that are always available
export const webSafeFonts = [
  'Arial, sans-serif',
  'Helvetica, Arial, sans-serif',
  'Georgia, serif',
  'Times New Roman, serif',
  'Courier New, monospace',
  'Verdana, sans-serif',
  'Trebuchet MS, sans-serif',
  'Impact, sans-serif',
  'Palatino, serif',
  'Lucida Console, monospace',
  'Tahoma, sans-serif',
];

// Function to get available custom fonts from public/fonts
export const getAvailableCustomFonts = async (): Promise<string[]> => {
  try {
    const customFonts = [
      'Graduate',
      'Josefin Sans',
      'Lato',
      'Merriweather',
      'Montserrat',
      'Open Sans',
      'Oswald',
      'Playfair Display',
      'Poppins',
      'Raleway',
      'Raleway Dots',
      'Roboto',
      'Source Sans 3',
      'Ubuntu',
    ];

    return customFonts;
  } catch (error) {
    console.error('Error loading custom fonts:', error);
    return [];
  }
};

// Google Fonts that should be loaded via CDN
const GOOGLE_FONTS_CDN = [
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Source Sans Pro',
  'Oswald',
  'Raleway',
  'PT Sans',
  'Ubuntu',
  'Nunito',
  'Playfair Display',
  'Merriweather',
  'Crimson Text',
  'Dancing Script',
  'Pacifico',
  'Righteous',
  'Lobster',
  'Indie Flower',
  'Shadows Into Light',
  'Josefin Sans',
  'Anton',
  'Bebas Neue',
  'Cabin',
  'Fjalla One',
];

// Track loaded fonts to prevent duplicates
const loadedFonts = new Set<string>();

// Function to load Google Fonts via CDN
export const loadGoogleFont = (fontName: string): Promise<void> => {
  return new Promise((resolve) => {
    // Check if already loaded
    if (loadedFonts.has(fontName)) {
      resolve();
      return;
    }

    // Create Google Fonts link if it doesn't exist
    const fontNameForUrl = fontName.replace(/\s+/g, '+');
    const linkId = `google-font-${fontNameForUrl.toLowerCase()}`;

    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.href = `https://fonts.googleapis.com/css2?family=${fontNameForUrl}:wght@300;400;500;600;700&display=swap`;
      link.rel = 'stylesheet';
      document.head.appendChild(link);

      link.onload = () => {
        loadedFonts.add(fontName);
        setTimeout(() => {
          resolve();
        }, 100);
      };

      link.onerror = () => {
        resolve(); // Don't reject, just continue
      };
    } else {
      loadedFonts.add(fontName);
      resolve();
    }
  });
};

// Function to load a custom font from local files
export const loadCustomFont = (fontName: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (loadedFonts.has(fontName)) {
      resolve();
      return;
    }

    // Check if font is already loaded in document
    if (document.fonts.check(`12px "${fontName}"`)) {
      loadedFonts.add(fontName);
      resolve();
      return;
    }

    // Special handling for Graduate font and other fonts with different naming patterns
    const getFontVariants = (fontName: string) => {
      if (fontName === 'Graduate') {
        return [
          { weight: '400', style: 'normal', filename: 'Graduate-Regular' },
        ];
      }

      // Default pattern for other fonts
      return [
        { weight: '400', style: 'normal', filename: `${fontName}-400` },
        { weight: '700', style: 'normal', filename: `${fontName}-700` },
        { weight: '400', style: 'italic', filename: `${fontName}-400i` },
        { weight: '700', style: 'italic', filename: `${fontName}-700i` },
      ];
    };

    const variants = getFontVariants(fontName);

    const loadPromises = variants.map((variant) => {
      const fontUrl = `/fonts/${fontName}/${variant.filename}`;

      const fontFace = new FontFace(
        fontName,
        `url(${fontUrl}.woff2) format('woff2'),
         url(${fontUrl}.woff) format('woff'),
         url(${fontUrl}.ttf) format('truetype')`,
        {
          weight: variant.weight,
          style: variant.style,
        }
      );

      return fontFace
        .load()
        .then(() => {
          document.fonts.add(fontFace);
          console.log(
            `Successfully loaded ${fontName} ${variant.weight} ${variant.style}`
          );
        })
        .catch((error) => {
          console.warn(
            `Failed to load ${fontName} variant ${variant.filename}:`,
            error
          );
          // Continue with other variants even if one fails
        });
    });

    Promise.allSettled(loadPromises)
      .then((results) => {
        // Check if at least one variant loaded successfully
        const hasSuccessful = results.some(
          (result) => result.status === 'fulfilled'
        );
        if (hasSuccessful) {
          loadedFonts.add(fontName);
          console.log(`Font ${fontName} loaded successfully`);
        } else {
          console.warn(`No variants of font ${fontName} could be loaded`);
        }
        resolve();
      })
      .catch((error) => {
        console.error(`Error loading font ${fontName}:`, error);
        resolve(); // Don't reject, just continue
      });
  });
};

// Extract unique font families from theme configuration
export const extractFontsFromTheme = (themeData: any): string[] => {
  const fonts = new Set<string>();

  // Check typography settings
  if (themeData.typography) {
    Object.entries(themeData.typography).forEach(
      ([key, style]: [string, any]) => {
        if (style && typeof style === 'object' && style.fontFamily) {
          const fontFamily = style.fontFamily as string;
          // Extract first font name from font stack
          const fontMatch = fontFamily.match(/^"?([^",;]+)"?/);
          if (fontMatch) {
            const fontName = fontMatch[1].trim();
            // Only add if it's not a generic font family
            if (
              ![
                'sans-serif',
                'serif',
                'monospace',
                'cursive',
                'fantasy',
                'Arial',
                'Helvetica',
              ].includes(fontName)
            ) {
              fonts.add(fontName);
            }
          }
        }
      }
    );
  }

  return Array.from(fonts);
};

// Load fonts used in current theme
export const loadThemeFonts = async (themeData: any): Promise<void> => {
  const fontsInTheme = extractFontsFromTheme(themeData);
  const customFonts = await getAvailableCustomFonts();

  // Load fonts based on whether they're Google Fonts or local custom fonts
  const loadPromises = fontsInTheme.map((fontName) => {
    if (GOOGLE_FONTS_CDN.includes(fontName)) {
      return loadGoogleFont(fontName);
    } else if (customFonts.includes(fontName)) {
      return loadCustomFont(fontName);
    } else {
      return Promise.resolve();
    }
  });

  await Promise.allSettled(loadPromises);
};

// Get all available fonts (web-safe + custom)
export const getAllAvailableFonts = async (): Promise<
  { label: string; value: string; isCustom: boolean }[]
> => {
  const customFonts = await getAvailableCustomFonts();

  const webSafeOptions = webSafeFonts.map((font) => ({
    label: font.split(',')[0], // Display just the first font name
    value: font,
    isCustom: false,
  }));

  // Include both local custom fonts and Google Fonts
  const allCustomFonts = Array.from(
    new Set([...customFonts, ...GOOGLE_FONTS_CDN])
  );

  const customOptions = allCustomFonts.map((font) => ({
    label: font,
    value: `"${font}", sans-serif`,
    isCustom: true,
  }));

  return [
    { label: 'Default', value: '', isCustom: false },
    ...webSafeOptions,
    ...customOptions,
  ];
};

// Add CSS font-face declarations as fallback for mobile
export const addFontFaceCSS = (): void => {
  const styleId = 'custom-font-faces';

  // Don't add if already exists
  if (document.getElementById(styleId)) {
    return;
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @font-face {
      font-family: 'Graduate';
      src: url('/fonts/Graduate/Graduate-Regular.ttf') format('truetype');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
    
    /* Add more font-face declarations for other fonts as needed */
  `;

  document.head.appendChild(style);
  console.log(
    'Added CSS font-face declarations for better mobile compatibility'
  );
};

// Initialize fonts for the site from site data
export const initializeSiteFonts = async (siteData: any): Promise<void> => {
  try {
    // Add CSS font-face declarations first for better mobile compatibility
    addFontFaceCSS();

    if (siteData?.theme) {
      await loadThemeFonts(siteData.theme);
    }
  } catch (error) {
    console.error('Error initializing site fonts:', error);
  }
};
